import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, MapPin, Trash2, Pencil } from "lucide-react";

const SITE_TYPES = ["production", "warehouse", "office", "distribution", "farm", "other"];
const FREQS = ["weekly", "biweekly", "monthly", "daily", "on_demand"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const empty = { name: "", address: "", site_type: "production", active: true, latitude: "", longitude: "", pickup_days: [], pickup_frequency: "weekly", pickup_time: "" };

export default function ClientSites({ clientId, canEdit }) {
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get(`/sites?client_id=${clientId}`).then((r) => setSites(r.data));
  useEffect(() => { if (clientId) load(); }, [clientId]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        client_id: clientId,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        pickup_time: form.pickup_time || null,
      };
      if (editingId) {
        await api.put(`/sites/${editingId}`, payload);
        toast.success("Site updated");
      } else {
        await api.post("/sites", payload);
        toast.success("Site added");
      }
      setForm(empty); setEditingId(null); setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (s) => {
    setForm({
      name: s.name, address: s.address || "", site_type: s.site_type, active: s.active,
      latitude: s.latitude ?? "", longitude: s.longitude ?? "",
      pickup_days: s.pickup_days || [], pickup_frequency: s.pickup_frequency || "weekly",
      pickup_time: s.pickup_time || "",
    });
    setEditingId(s.id); setShowForm(true);
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete site "${s.name}"?`)) return;
    await api.delete(`/sites/${s.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="hbt-label flex items-center gap-2"><MapPin className="w-3 h-3" /> Sites ({sites.length})</div>
        {canEdit && (
          <button onClick={() => { setForm(empty); setEditingId(null); setShowForm(true); }} className="text-xs font-semibold text-[var(--forest)] hover:underline flex items-center gap-1" data-testid={`add-site-${clientId}`}>
            <Plus className="w-3 h-3" /> Add site
          </button>
        )}
      </div>
      <div className="space-y-2">
        {sites.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 text-xs py-1.5 px-2 rounded hover:bg-black/[0.02]" data-testid={`site-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{s.name}</div>
              <div className="text-neutral-500 text-[11px] flex items-center gap-2">
                <span className="capitalize">{s.site_type}</span>
                {s.address && <><span>·</span><span className="truncate">{s.address}</span></>}
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => edit(s)} className="p-1 text-neutral-500 hover:text-black" data-testid={`edit-site-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(s)} className="p-1 text-neutral-500 hover:text-red-700" data-testid={`delete-site-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        ))}
        {sites.length === 0 && <div className="text-xs text-neutral-400 py-2">No sites yet.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="hbt-card p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="site-form-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">{editingId ? "Edit" : "New"} site</div>
                <div className="font-display text-xl font-semibold mt-1">{editingId ? "Update site" : "Add a site"}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-black"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="hbt-label block mb-2">Site name</label>
                <input required className="hbt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name-input" />
              </div>
              <div>
                <label className="hbt-label block mb-2">Type</label>
                <select className="hbt-input" value={form.site_type} onChange={(e) => setForm({ ...form, site_type: e.target.value })} data-testid="site-type-select">
                  {SITE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="hbt-label block mb-2">Address</label>
                <input className="hbt-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="site-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Latitude</label>
                  <input className="hbt-input" placeholder="-26.4931" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="site-lat-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Longitude</label>
                  <input className="hbt-input" placeholder="31.3200" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="site-lng-input" />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Pickup frequency</label>
                <select className="hbt-input" value={form.pickup_frequency} onChange={(e) => setForm({ ...form, pickup_frequency: e.target.value })} data-testid="site-freq-select">
                  {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              {form.pickup_frequency !== "daily" && form.pickup_frequency !== "on_demand" && (
                <div>
                  <label className="hbt-label block mb-2">Pickup days</label>
                  <div className="flex gap-1 flex-wrap" data-testid="site-days">
                    {DAY_LABELS.map((lbl, idx) => {
                      const on = form.pickup_days.includes(idx);
                      return (
                        <button
                          key={idx} type="button"
                          onClick={() => setForm({ ...form, pickup_days: on ? form.pickup_days.filter((d) => d !== idx) : [...form.pickup_days, idx].sort() })}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${on ? "bg-[var(--forest)] text-[var(--bone)] border-transparent" : "border-[var(--border)] text-neutral-600"}`}
                          data-testid={`site-day-${idx}`}
                        >{lbl}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="site-active-check" />
                <span>Active</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="save-site">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
